export interface MetricsOverview {
  totalUsers: number
  totalByRole: Record<string, number>
  usersLoggedIn: number
  usersNeverLoggedIn: number
  usersChangedPasswordRecently: number
  passwordChangeWindowDays: number
  totalReviews: number
  reviewsCompleted: number
  reviewsInProgress: number
  reviewsSkipped: number
}

export interface ReviewMetrics {
  after: string
  before: string
  reviewsCompleted: number
  reviewsInProgress: number
  reviewsSkipped: number
  talentRankTenX: number
  talentRankNineX: number
  talentRankCore: number
  talentRankMismatch: number
}

export interface UserMetricsTalentRankBreakdown {
  tenX: number
  nineX: number
  core: number
  mismatch: number
}

export interface UserMetrics {
  id: number
  name: string
  email: string
  hasLoggedIn: boolean
  lastLoginDate: string | null
  totalReviewsAssigned: number
  reviewsCompleted: number
  reviewsInProgress: number
  reviewsNotStarted: number
  reviewsSkipped: number
  completedByTalentRank: UserMetricsTalentRankBreakdown
}

export interface PagedUserMetrics {
  content: UserMetrics[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

// ─── Dimension metrics ──────────────────────────────────────────────────────

export interface DimensionScores {
  growthMindset: number | null
  communication: number | null
  commitment: number | null
  professionalSkills: number | null
  productivity: number | null
}

export interface CategoryDimensionScores extends DimensionScores {
  category: string
  reviewCount: number
}

export interface DimensionPercentiles {
  p25: DimensionScores
  p50: DimensionScores
  p75: DimensionScores
}

export interface DimensionMetrics {
  overall: DimensionScores
  percentiles: DimensionPercentiles
  byCategory: CategoryDimensionScores[]
}

// ─── Leader metrics ─────────────────────────────────────────────────────────

export interface LeaderMetrics {
  id: number
  name: string
  reportCount: number
  avgScore: number | null
  tenX: number
  nineX: number
  core: number
  mismatch: number
}

// ─── AI brief ───────────────────────────────────────────────────────────────

export interface AiBrief {
  brief: string
  aiGenerated: boolean
}

// ─── Time range ─────────────────────────────────────────────────────────────

export type PresetRange = '24h' | '7d' | '30d' | '90d'
export type TimeRange = PresetRange | 'custom'

export const PRESET_RANGE_LABELS: Record<PresetRange, string> = {
  '24h': 'Last 24h',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
}

export const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  ...PRESET_RANGE_LABELS,
  custom: 'Custom',
}

export function timeRangeToParams(
  range: TimeRange,
  customAfter?: string,
  customBefore?: string
): { after: string; before: string } | null {
  if (range === 'custom') {
    if (!customAfter || !customBefore) return null
    return { after: `${customAfter}T00:00:00`, before: `${customBefore}T23:59:59` }
  }

  const now = new Date()
  const before = now.toISOString().split('.')[0]

  const after = new Date(now)
  switch (range) {
    case '24h':
      after.setHours(after.getHours() - 24)
      break
    case '7d':
      after.setDate(after.getDate() - 7)
      break
    case '30d':
      after.setDate(after.getDate() - 30)
      break
    case '90d':
      after.setDate(after.getDate() - 90)
      break
  }

  return { after: after.toISOString().split('.')[0], before }
}

export function formatDateLabel(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── Completed reviews ───────────────────────────────────────────────────────

export type TalentRankKey = 'TEN_X' | 'NINE_X' | 'CORE' | 'MISMATCH'

export interface CompletedReview {
  reviewId: number
  employeeId: number
  employeeName: string
  employeeEmail: string
  reviewerId: number
  reviewerName: string
  periodId: number
  periodName: string
  finishDate: string
  talentRank: TalentRankKey | null
  averageScore: number | null
}

export interface PagedCompletedReviews {
  content: CompletedReview[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

// ─── Skipped reviews ─────────────────────────────────────────────────────────

export interface SkippedReview {
  skipId: number
  employeeId: number
  employeeName: string
  employeeEmail: string
  reviewerId: number
  reviewerName: string
  periodId: number
  periodName: string
  skipDate: string
  reason: string
  notes: string | null
}

export interface PagedSkippedReviews {
  content: SkippedReview[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}
